import { useMutation } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { type DealNotice, dealNoticeFor } from '../../deals/components/deal-next-action';
import { addBidToLedger } from '../api';
import { cn, localVehicleHref } from '../utils';

export interface AddToLedgerSectionProps {
  bidId: string;
  /** The ledger card the bid already became (GET /bids/:id `localVehicleId`), or null. */
  localVehicleId: string | null;
  /** Re-read the bid detail once the card exists, so the page and the queue agree. */
  onAdded: () => void;
}

/**
 * «Облік» on a WON bid of an ADMIN bidder (local-ledger bridge) — the owners
 * bought this car for themselves, so it is never invoiced: it goes to their
 * own trading book on local.cars-finders.com.
 *
 * Before: «Додати до обліку» → `POST /admin/bids/:bidId/local-vehicle`, then a
 * toast and a refetch of the detail. After (or when the bid already became a
 * card): «В обліку» and «Відкрити в обліку», a link to the card in a new tab
 * (the ledger has its own session). The card is shown from the answer itself,
 * not only after the refetch, so the link never lags the click. Refusals are
 * the same sentences the «Угоди» row shows (`dealNoticeFor`).
 */
export function AddToLedgerSection({ bidId, localVehicleId, onAdded }: AddToLedgerSectionProps) {
  const [added, setAdded] = useState<string | null>(null);
  const [notice, setNotice] = useState<DealNotice | null>(null);
  const mutation = useMutation({ mutationFn: () => addBidToLedger(bidId) });
  const vehicleId = added ?? localVehicleId;

  const add = async () => {
    if (mutation.isPending) return;
    setNotice(null);
    try {
      const { vehicle, existing } = await mutation.mutateAsync();
      setAdded(vehicle.id);
      toast.success(
        t(existing ? 'admin.deals.notice.alreadyInLedger' : 'admin.deals.notice.addedToLedger'),
      );
      onAdded();
    } catch (error) {
      const next = dealNoticeFor(error);
      setNotice(next);
      if (next.refresh) onAdded();
    }
  };

  return (
    <section
      data-testid="bid-ledger-section"
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5"
    >
      <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
        {t('bids.dAdmin.detailPage.ledger.title')}
      </h2>
      {vehicleId ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="inline-flex rounded-cf-sm bg-cf-info-tint px-2 py-1 font-mono text-[10px] tracking-[0.08em] text-cf-info-ink uppercase">
            {t('bids.dAdmin.detailPage.ledger.inLedger')}
          </span>
          <a
            href={localVehicleHref(vehicleId)}
            target="_blank"
            rel="noreferrer"
            data-testid="bid-ledger-link"
            aria-label={t('bids.dAdmin.detailPage.ledger.openAria')}
            className="inline-flex items-center gap-1 rounded-cf-sm text-[13px] font-semibold text-cf-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-brand"
          >
            {t('bids.dAdmin.detailPage.ledger.open')}
            <ExternalLink size={13} aria-hidden="true" />
          </a>
        </div>
      ) : (
        <>
          <p className="mt-1.5 text-[13px] text-cf-ink-muted">
            {t('bids.dAdmin.detailPage.ledger.hint')}
          </p>
          <div className="mt-3">
            <button
              type="button"
              data-testid="bid-add-to-ledger"
              onClick={() => void add()}
              disabled={mutation.isPending}
              className="h-10 rounded-cf-md border border-cf-brand bg-cf-brand px-3.5 text-[13px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-brand disabled:opacity-50"
            >
              {mutation.isPending
                ? t('bids.dAdmin.detailPage.ledger.adding')
                : t('bids.dAdmin.detailPage.ledger.add')}
            </button>
          </div>
        </>
      )}
      {notice && (
        <p
          role={notice.tone === 'danger' ? 'alert' : 'status'}
          className={cn(
            'mt-3 rounded-cf-md border px-2.5 py-1.5 text-[12px] leading-[1.4]',
            notice.tone === 'info'
              ? 'border-cf-info bg-cf-info-tint text-cf-info-ink'
              : 'border-cf-danger bg-cf-danger-tint text-cf-danger',
          )}
        >
          {notice.text}
        </p>
      )}
    </section>
  );
}
